import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { systemDesignApi } from "../api/systemDesignApi";
import { SystemDesign } from "../types/SystemDesign";
import { 
  Trash2, 
  Search, 
  Plus, 
  Building2, 
  FileText, 
  Clock, 
  AlertCircle,
  ArrowRight,
  LayoutGrid,
  List
} from "lucide-react";

const SystemDesignPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [designs, setDesigns] = useState<SystemDesign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    setIsLoading(true);
    try {
      const fetchedDesigns = await systemDesignApi.getAll();
      setDesigns(fetchedDesigns);
    } catch (error) {
      console.error("Error fetching system designs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDesigns = designs.filter(
    (design) =>
      design.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      design.company.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDeleteClick = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      try {
        await systemDesignApi.delete(deleteId);
        setDesigns(designs.filter((design) => design.id !== deleteId));
      } catch (error) {
        console.error("Error deleting system design:", error);
      }
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const getDifficultyStyle = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case "easy": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "medium": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "hard": return "bg-red-500/20 text-red-400 border-red-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const stats = {
    total: designs.length,
    easy: designs.filter(d => d.difficulty?.toLowerCase() === "easy").length,
    medium: designs.filter(d => d.difficulty?.toLowerCase() === "medium").length,
    hard: designs.filter(d => d.difficulty?.toLowerCase() === "hard").length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>System Design - AlgoBuddy</title>
        <meta name="description" content="Practice and learn system design questions for technical interviews." />
      </Helmet>

      <main className="min-h-screen bg-gray-950 p-4 md:p-8">
        {/* Header Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 hover:border-gray-700 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{stats.total}</p>
                <p className="text-[10px] text-gray-500 leading-tight">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 hover:border-gray-700 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <FileText className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{stats.easy}</p>
                <p className="text-[10px] text-gray-500 leading-tight">Easy</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 hover:border-gray-700 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <FileText className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{stats.medium}</p>
                <p className="text-[10px] text-gray-500 leading-tight">Medium</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 hover:border-gray-700 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                <FileText className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{stats.hard}</p>
                <p className="text-[10px] text-gray-500 leading-tight">Hard</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-white">System Design</h1>
                <p className="text-gray-500 text-sm mt-1">Master scalable system architecture</p>
              </div>
              <button
                onClick={() => navigate("/system-design/new")}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-medium rounded-xl transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by title or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "cards" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-white"}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`p-2 rounded-md transition-colors ${viewMode === "table" ? "bg-gray-700 text-white" : "text-gray-400 hover:text-white"}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {filteredDesigns.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No questions found</h3>
                <p className="text-gray-500 mb-6">Start by adding your first system design question.</p>
                <button
                  onClick={() => navigate("/system-design/new")}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
            ) : viewMode === "cards" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDesigns.map((design) => (
                  <div
                    key={design.id}
                    onClick={() => navigate(`/system-design/${design.id}`)}
                    className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-4 h-4 text-blue-400" />
                          <span className="text-xs text-blue-400 font-medium">{design.company}</span>
                        </div>
                        <h3 className="font-semibold text-white group-hover:text-emerald-400 transition-colors line-clamp-2">
                          {design.title}
                        </h3>
                      </div>
                      <button
                        onClick={(e) => handleDeleteClick(design.id, e)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {design.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getDifficultyStyle(design.difficulty)}`}>
                        {design.difficulty}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {filteredDesigns.map((design) => (
                      <tr
                        key={design.id}
                        onClick={() => navigate(`/system-design/${design.id}`)}
                        className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                      >
                        <td className="py-4 px-4">
                          <span className="text-blue-400 font-medium">{design.company}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white font-medium">{design.title}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-gray-400 text-sm line-clamp-1">{design.description}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getDifficultyStyle(design.difficulty)}`}>
                            {design.difficulty}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <button
                            onClick={(e) => handleDeleteClick(design.id, e)}
                            className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Delete Question</h3>
                  <p className="text-sm text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete this system design question? This will remove it permanently.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-700 hover:border-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default SystemDesignPage;